import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import AttachmentsPanel from '../../components/AttachmentsPanel';
import BookStageBar from '../../components/books/BookStageBar';
import NoteItemsPanel from '../../components/books/NoteItemsPanel';
import { KIND_AR, STAGE_AR, STAGE_BADGE, canUpload, stageAction } from '../../components/books/BookStatus';
import { fmtDateBoth, fmtDateShort, fmtDateTimeBoth } from '../../utils/dates';

const KINDS = ['source', 'researcher', 'reviewer', 'final', 'pending_notes'];

const today = () => new Date().toISOString().slice(0, 10);

export default function BookSummaryDetail() {
  const { id } = useParams();
  const { api } = useAuth();
  const toast = useToast();

  const [data, setData] = useState(null);
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null); // { key, ...حقول }
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api(`/book-summaries/${id}`)
      .then(setData)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [api, id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api('/book-summaries/people?active_only=1').then(setPeople).catch(() => {});
  }, []);

  if (loading) return <div className="loader" />;
  if (!data) {
    return (
      <div className="card">
        <div className="empty"><div className="empty-icon">📕</div>الكتاب غير موجود أو غير مصرّح لك</div>
      </div>
    );
  }

  const { summary, rounds, notes, attachments, events, abilities } = data;
  const projectPeople = people.filter(p => p.project_id === summary.project_id);
  const researchers = projectPeople.filter(p => p.can_summarize);
  const reviewers = projectPeople.filter(p => p.can_review);
  const action = stageAction(summary.status, abilities);
  const pendingCount = notes.filter(n => n.status === 'pending').length;
  const untouched = notes.filter(n => n.status === 'new').length;
  const hasPendingFile = attachments.some(a => a.kind === 'pending_notes');

  const post = async (path, body) => {
    setBusy(true);
    try {
      await api(`/book-summaries/${summary.id}/${path}`, {
        method: 'POST',
        body: JSON.stringify(body || {}),
      });
      setDialog(null);
      load();
      return true;
    } catch (err) {
      toast.error(err.message);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const openDialog = (key) => {
    const defaults = {
      assign: { researcher_person_id: summary.researcher_person_id || '', due_date: '' },
      'submit-summary': { reviewer_person_id: '', due_date: '' },
      'submit-review': { outcome: 'notes', due_date: '' },
      'submit-revision': {},
      approve: { acknowledge_pending: false, note: '' },
      reject: { target: 'revision', reason: '', reviewer_person_id: '', due_date: '' },
      cancel: { reason: '' },
      reopen: { reason: '', due_date: '' },
      deadline: { due_date: summary.stage_due_date || today(), reason: '' },
    };
    setDialog({ key, ...defaults[key] });
  };

  const submitDialog = async () => {
    const d = dialog;
    switch (d.key) {
      case 'assign':
        return post('assign', {
          researcher_person_id: Number(d.researcher_person_id),
          due_date: d.due_date || null,
        });
      case 'submit-summary':
        return post('submit-summary', {
          reviewer_person_id: Number(d.reviewer_person_id),
          due_date: d.due_date || null,
        });
      case 'submit-review':
        return post('submit-review', { outcome: d.outcome, due_date: d.due_date || null });
      case 'submit-revision':
        return post('submit-revision', {});
      case 'approve':
        return post('approve', { acknowledge_pending: d.acknowledge_pending, note: d.note || null });
      case 'reject':
        return post('reject', {
          target: d.target,
          reason: d.reason,
          reviewer_person_id: d.target === 'review' ? Number(d.reviewer_person_id) : null,
          due_date: d.due_date || null,
        });
      case 'cancel':
        return post('cancel', { reason: d.reason });
      case 'reopen':
        return post('reopen', { reason: d.reason, due_date: d.due_date || null });
      case 'deadline': {
        setBusy(true);
        try {
          await api(`/book-summaries/${summary.id}/deadline`, {
            method: 'PUT',
            body: JSON.stringify({ due_date: d.due_date, reason: d.reason }),
          });
          setDialog(null);
          load();
        } catch (err) {
          toast.error(err.message);
        } finally {
          setBusy(false);
        }
        return true;
      }
      default:
        return false;
    }
  };

  return (
    <div>
      <div style={{ marginBottom: 8 }}>
        <Link to="/book-summaries" style={{ fontSize: 13 }}>← تلخيص الكتب</Link>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>{summary.book_title}</h2>
        <span className={`badge badge-${STAGE_BADGE[summary.status]}`}>{STAGE_AR[summary.status]}</span>
        {summary.is_overdue && <span className="badge badge-cancelled">متأخّر</span>}

        <div style={{ marginRight: 'auto', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {/* زرّ إجراء واحد يتغيّر بالمرحلة — لا خمسة أزرار تسمح بالتخطّي */}
          {action && (
            <button className="btn btn-primary" onClick={() => openDialog(action.key)}>
              {action.icon} {action.label}
            </button>
          )}
          {abilities.approve && summary.status === 'awaiting_approval' && (
            <button className="btn btn-outline" onClick={() => openDialog('reject')}>↩ إرجاع</button>
          )}
          {abilities.manage && !['approved', 'cancelled'].includes(summary.status) && (
            <>
              <button className="btn btn-outline btn-sm" onClick={() => openDialog('deadline')}>⏱ تمديد المهلة</button>
              <button className="btn btn-outline btn-sm" onClick={() => openDialog('cancel')}>إلغاء الكتاب</button>
            </>
          )}
          {abilities.manage && summary.status === 'approved' && (
            <button className="btn btn-outline btn-sm" onClick={() => openDialog('reopen')}>↺ إعادة فتح</button>
          )}
        </div>
      </div>

      {/* بيانات الكتاب */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-title" style={{ marginBottom: 12 }}>📕 بيانات الكتاب</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14 }}>
          <Field label="المشروع" value={summary.project_title} />
          <Field label="المؤلّف" value={summary.book_author} />
          <Field label="الطبعة" value={summary.book_edition} />
          <Field label="عدد الصفحات" value={summary.book_pages} />
          <Field label="الرمز" value={summary.code} />
          <Field label="الباحث" value={summary.researcher_name} />
          <Field label="مراجع الجولة" value={summary.reviewer_name} />
          <Field
            label="مهلة المرحلة"
            value={summary.stage_due_date ? fmtDateShort(summary.stage_due_date) : null}
            danger={summary.is_overdue}
          />
          <Field label="عدد الجولات" value={summary.rounds_count || '0'} />
          {summary.approved_at && (
            <Field label="اعتُمد" value={`${fmtDateBoth(summary.approved_at)}${summary.approved_by_name ? ` — ${summary.approved_by_name}` : ''}`} />
          )}
        </div>
        {summary.description && (
          <div style={{ marginTop: 12, fontSize: 13, color: 'var(--text-muted)', whiteSpace: 'pre-wrap' }}>
            {summary.description}
          </div>
        )}
      </div>

      {/* شريط المراحل */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-title" style={{ marginBottom: 16 }}>🧭 مسار الملف</div>
        <BookStageBar summary={summary} />
      </div>

      {/* الجولات والمهل */}
      {rounds.length > 0 && (
        <div className="card" style={{ marginBottom: 20, padding: 0 }}>
          <div className="card-title" style={{ padding: '14px 14px 0' }}>🔁 الجولات والمهل</div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 60 }}>الجولة</th>
                  <th>فتحها</th>
                  <th>المراجع</th>
                  <th>مهلة المراجعة</th>
                  <th>سُلِّمت</th>
                  <th>مهلة التعديل</th>
                  <th>أعادها الباحث</th>
                  <th>النتيجة</th>
                </tr>
              </thead>
              <tbody>
                {rounds.map(r => (
                  <tr key={r.id}>
                    <td style={{ fontWeight: 600 }}>{r.round_no}</td>
                    <td style={{ fontSize: 12 }}>{r.opened_by === 'supervisor' ? 'المشرف' : 'الباحث'}</td>
                    <td style={{ fontSize: 13 }}>{r.reviewer?.name || '—'}</td>
                    <td style={{ fontSize: 12 }}>{r.review_due_date ? fmtDateShort(r.review_due_date) : '—'}</td>
                    <td style={{ fontSize: 12 }}>{r.reviewer_submitted_at ? fmtDateShort(r.reviewer_submitted_at) : '—'}</td>
                    <td style={{ fontSize: 12 }}>{r.revise_due_date ? fmtDateShort(r.revise_due_date) : '—'}</td>
                    <td style={{ fontSize: 12 }}>{r.researcher_submitted_at ? fmtDateShort(r.researcher_submitted_at) : '—'}</td>
                    <td style={{ fontSize: 12 }}>
                      {r.reviewer_outcome === 'pass' ? 'اجتاز بلا ملاحظات'
                        : r.reviewer_outcome === 'notes' ? 'ملاحظات'
                        : r.reason || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* بنود الملاحظات */}
      <div className="card" style={{ marginBottom: 20 }} data-tour="books-notes">
        <NoteItemsPanel
          summary={summary}
          notes={notes}
          rounds={rounds}
          abilities={abilities}
          onChange={load}
        />
      </div>

      {/* نُسخ الملف */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-title" style={{ marginBottom: 4 }}>📎 نُسخ الملف</div>
        <div style={{ fontSize: 12, color: 'var(--text-faint)', marginBottom: 12 }}>
          كل نسخة تُحفظ بترقيمها، والروابط موقَّعة مؤقّتة.
        </div>
        {KINDS.map(kind => (
          <div key={kind} style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
              {KIND_AR[kind]}
              {kind === 'pending_notes' && pendingCount > 0 && !hasPendingFile
                && summary.status !== 'approved' && (
                <span style={{ color: 'var(--danger)', fontWeight: 400 }}> — مطلوب مع {pendingCount} بنداً معلّقاً</span>
              )}
            </div>
            <AttachmentsPanel
              basePath={`/book-summaries/${summary.id}/attachments/${kind}`}
              attachments={attachments.filter(a => a.kind === kind)}
              onChange={load}
              canEdit={canUpload(kind, summary.status, abilities)}
              hint={`اسحب ملفات هنا لرفع ${KIND_AR[kind]}`}
              emptyText={`لم تُرفع ${KIND_AR[kind]}`}
            />
          </div>
        ))}
      </div>

      {/* سجلّ الأحداث */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-title" style={{ marginBottom: 12 }}>🕓 سجلّ الأحداث</div>
        {events.length === 0 ? (
          <div style={{ color: 'var(--text-faint)', fontSize: 13 }}>لا أحداث بعد</div>
        ) : events.map(e => (
          <div key={e.id} className="log-item">
            <div className="log-dot" />
            <div>
              <div style={{ fontSize: 13 }}>
                {eventText(e)}
                {/* الفاعل والمنسوب إليه يفترقان في وضع التسجيل نيابةً */}
                {e.on_behalf?.name && e.on_behalf.name !== e.actor?.name && (
                  <span style={{ color: 'var(--text-faint)' }}>
                    {' '}— عن {e.on_behalf.name}
                  </span>
                )}
              </div>
              {e.note && (
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{e.note}</div>
              )}
              <div className="log-time">
                {e.actor?.name || 'النظام'} · {fmtDateTimeBoth(e.created_at)}
              </div>
            </div>
          </div>
        ))}
      </div>

      {dialog && (
        <div className="modal-overlay" onClick={() => setDialog(null)}>
          <div className="modal" onClick={ev => ev.stopPropagation()}>
            <div className="modal-title">{DIALOG_TITLES[dialog.key]}</div>

            {dialog.key === 'assign' && (
              <>
                <PersonSelect
                  id="bs-researcher"
                  label="الباحث *"
                  people={researchers}
                  value={dialog.researcher_person_id}
                  onChange={v => setDialog({ ...dialog, researcher_person_id: v })}
                  emptyHint="لا باحثين في فريق هذا المشروع — أضفهم من «فريق العمل»"
                />
                <DateField
                  id="bs-summary-due"
                  label="مهلة التلخيص"
                  value={dialog.due_date}
                  onChange={v => setDialog({ ...dialog, due_date: v })}
                  hint="تُترك فارغة = ٣٠ يوماً"
                />
              </>
            )}

            {dialog.key === 'submit-summary' && (
              <>
                <PersonSelect
                  id="bs-reviewer"
                  label="المراجع من لجنة المراجعة *"
                  people={reviewers}
                  value={dialog.reviewer_person_id}
                  onChange={v => setDialog({ ...dialog, reviewer_person_id: v })}
                  emptyHint="لا مراجعين في فريق هذا المشروع — أضفهم من «فريق العمل»"
                />
                <DateField
                  id="bs-review-due"
                  label="مهلة المراجعة"
                  value={dialog.due_date}
                  onChange={v => setDialog({ ...dialog, due_date: v })}
                  hint="تُترك فارغة = ١٠ أيام"
                />
              </>
            )}

            {dialog.key === 'submit-review' && (
              <>
                <div className="form-group">
                  <label className="form-label" htmlFor="bs-outcome">نتيجة المراجعة</label>
                  <select
                    id="bs-outcome"
                    className="form-select"
                    value={dialog.outcome}
                    onChange={e => setDialog({ ...dialog, outcome: e.target.value })}
                  >
                    <option value="notes">هناك ملاحظات — يعود للباحث</option>
                    <option value="pass">اجتاز بلا ملاحظات — للاعتماد</option>
                  </select>
                </div>
                {dialog.outcome === 'notes' && (
                  <>
                    {notes.filter(n => n.round_id === summary.current_round_id || n.round_id === null).length === 0 && (
                      <div style={{ color: 'var(--danger)', fontSize: 13, marginBottom: 8 }}>
                        لم تُقيَّد أي ملاحظة في هذه الجولة — أضف بنداً أو اختر «اجتاز بلا ملاحظات».
                      </div>
                    )}
                    <DateField
                      id="bs-revise-due"
                      label="مهلة الباحث للتعديل"
                      value={dialog.due_date}
                      onChange={v => setDialog({ ...dialog, due_date: v })}
                      hint="تُترك فارغة = ٧ أيام"
                    />
                  </>
                )}
              </>
            )}

            {dialog.key === 'submit-revision' && (
              <div style={{ fontSize: 13, marginBottom: 10 }}>
                {untouched > 0 ? (
                  <span style={{ color: 'var(--danger)' }}>
                    هناك {untouched} بنداً لم تُعالجها — علّم كل بند بأنه عُدِّل قبل التسليم.
                  </span>
                ) : (
                  <>سيُسلَّم الكتاب للاعتماد النهائي{pendingCount > 0 ? ` مع ${pendingCount} بنداً معلّقاً.` : '.'}</>
                )}
              </div>
            )}

            {dialog.key === 'approve' && (
              <>
                {untouched > 0 && (
                  <div style={{ color: 'var(--danger)', fontSize: 13, marginBottom: 10 }}>
                    هناك {untouched} بنداً لم يُنظر فيها — لا يُعتمد الكتاب قبل حسمها.
                  </div>
                )}
                {pendingCount > 0 && (
                  <div className="form-group">
                    <div style={{ fontSize: 13, marginBottom: 6 }}>
                      هناك <b>{pendingCount}</b> بنداً معلّقاً سيُرفق مع الملف العام.
                      {!hasPendingFile && (
                        <span style={{ color: 'var(--danger)' }}> ولم يُرفع ملف الملاحظات المعلّقة بعد.</span>
                      )}
                    </div>
                    <label style={{ fontSize: 13 }}>
                      <input
                        type="checkbox"
                        checked={dialog.acknowledge_pending}
                        onChange={e => setDialog({ ...dialog, acknowledge_pending: e.target.checked })}
                      />{' '}
                      أقرّ بالبنود المعلّقة وأعتمد الكتاب
                    </label>
                  </div>
                )}
                <div className="form-group">
                  <label className="form-label" htmlFor="bs-approve-note">ملاحظة الاعتماد</label>
                  <textarea
                    id="bs-approve-note"
                    className="form-textarea"
                    rows={2}
                    value={dialog.note}
                    onChange={e => setDialog({ ...dialog, note: e.target.value })}
                  />
                </div>
              </>
            )}

            {dialog.key === 'reject' && (
              <>
                <div className="form-group">
                  <label className="form-label" htmlFor="bs-reject-target">إلى أين يعود الملف؟</label>
                  <select
                    id="bs-reject-target"
                    className="form-select"
                    value={dialog.target}
                    onChange={e => setDialog({ ...dialog, target: e.target.value })}
                  >
                    <option value="revision">إلى الباحث للتعديل</option>
                    <option value="review">إلى المراجعة من جديد</option>
                  </select>
                </div>
                {dialog.target === 'review' && (
                  <PersonSelect
                    id="bs-new-reviewer"
                    label="مراجع الجولة الجديدة *"
                    people={reviewers}
                    value={dialog.reviewer_person_id}
                    onChange={v => setDialog({ ...dialog, reviewer_person_id: v })}
                  />
                )}
                <DateField
                  id="bs-reject-due"
                  label="المهلة"
                  value={dialog.due_date}
                  onChange={v => setDialog({ ...dialog, due_date: v })}
                />
                <ReasonField
                  id="bs-reject-reason"
                  value={dialog.reason}
                  onChange={v => setDialog({ ...dialog, reason: v })}
                  label="سبب الإرجاع *"
                />
              </>
            )}

            {dialog.key === 'cancel' && (
              <ReasonField
                id="bs-cancel-reason"
                value={dialog.reason}
                onChange={v => setDialog({ ...dialog, reason: v })}
                label="سبب الإلغاء *"
              />
            )}

            {dialog.key === 'reopen' && (
              <>
                <div style={{ fontSize: 13, marginBottom: 10, color: 'var(--text-muted)' }}>
                  ستُسحب علامة الاعتماد وتُفتح جولة تعديل جديدة.
                </div>
                <DateField
                  id="bs-reopen-due"
                  label="مهلة التعديل"
                  value={dialog.due_date}
                  onChange={v => setDialog({ ...dialog, due_date: v })}
                />
                <ReasonField
                  id="bs-reopen-reason"
                  value={dialog.reason}
                  onChange={v => setDialog({ ...dialog, reason: v })}
                  label="سبب إعادة الفتح *"
                />
              </>
            )}

            {dialog.key === 'deadline' && (
              <>
                <DateField
                  id="bs-deadline-due"
                  label="المهلة الجديدة *"
                  value={dialog.due_date}
                  onChange={v => setDialog({ ...dialog, due_date: v })}
                />
                <ReasonField
                  id="bs-deadline-reason"
                  value={dialog.reason}
                  onChange={v => setDialog({ ...dialog, reason: v })}
                  label="سبب التمديد *"
                />
              </>
            )}

            <div className="modal-actions">
              <button className="btn btn-outline" onClick={() => setDialog(null)}>إلغاء</button>
              <button
                className="btn btn-primary"
                onClick={submitDialog}
                disabled={busy || !dialogValid(dialog, { untouched, pendingCount })}
              >
                {busy ? '...' : 'تنفيذ'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const DIALOG_TITLES = {
  assign: 'تكليف باحث بالتلخيص',
  'submit-summary': 'تسليم التلخيص للمراجعة',
  'submit-review': 'تسليم نتيجة المراجعة',
  'submit-revision': 'تسليم التعديلات للاعتماد',
  approve: 'الاعتماد النهائي',
  reject: 'إرجاع الملف',
  cancel: 'إلغاء الكتاب',
  reopen: 'إعادة فتح كتاب معتمد',
  deadline: 'تمديد مهلة المرحلة',
};

function dialogValid(d, { untouched, pendingCount }) {
  switch (d.key) {
    case 'assign': return !!d.researcher_person_id;
    case 'submit-summary': return !!d.reviewer_person_id;
    case 'submit-revision': return untouched === 0;
    case 'approve': return untouched === 0 && (pendingCount === 0 || d.acknowledge_pending);
    case 'reject':
      return !!d.reason.trim() && (d.target !== 'review' || !!d.reviewer_person_id);
    case 'cancel':
    case 'reopen': return !!d.reason.trim();
    case 'deadline': return !!d.due_date && !!d.reason.trim();
    default: return true;
  }
}

function eventText(e) {
  if (e.action === 'note_status_changed') return 'تغيير حالة بند';
  if (e.action === 'attachment_uploaded') return 'رفع مرفق';
  if (e.action === 'deadline_extended') return 'تمديد مهلة';
  const from = STAGE_AR[e.from_status] || e.from_status;
  const to = STAGE_AR[e.to_status] || e.to_status;
  return `${from} ← ${to}`;
}

function Field({ label, value, danger }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: 'var(--text-faint)' }}>{label}</div>
      <div style={{ fontSize: 14, color: danger ? 'var(--danger)' : undefined }}>{value || '—'}</div>
    </div>
  );
}

function PersonSelect({ id, label, people, value, onChange, emptyHint }) {
  return (
    <div className="form-group">
      <label className="form-label" htmlFor={id}>{label}</label>
      <select id={id} className="form-select" value={value} onChange={e => onChange(e.target.value)}>
        <option value="">اختر</option>
        {people.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
      </select>
      {people.length === 0 && emptyHint && (
        <div style={{ fontSize: 12, color: 'var(--danger)', marginTop: 4 }}>{emptyHint}</div>
      )}
    </div>
  );
}

function DateField({ id, label, value, onChange, hint }) {
  return (
    <div className="form-group">
      <label className="form-label" htmlFor={id}>{label}</label>
      <input id={id} className="form-input" type="date" value={value || ''} onChange={e => onChange(e.target.value)} />
      {hint && <div style={{ fontSize: 12, color: 'var(--text-faint)', marginTop: 4 }}>{hint}</div>}
    </div>
  );
}

function ReasonField({ id, label, value, onChange }) {
  return (
    <div className="form-group">
      <label className="form-label" htmlFor={id}>{label}</label>
      <textarea id={id} className="form-textarea" rows={2} value={value} onChange={e => onChange(e.target.value)} />
    </div>
  );
}
